import { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useQuickCreateRoute } from "@/hooks/useQuickCreateRoute";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Calendar as CalendarIcon,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  MapPin,
  Target,
  CheckSquare,
  Filter,
  Settings,
  RefreshCw,
  Trash2,
  ExternalLink,
  Download,
  Star,
  Gift,
  Repeat,
  Edit,
  X,
  Sparkles,
  Bot,
  Send,
  Loader2,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { formatDistanceToNow, format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import CreateEventModal from "@/components/create-event-modal";
import EventDetailsDialog from "@/components/event-details-dialog";
import { INTERNAL_CALENDARS_QUERY_KEY, type InternalCalendar } from "@/components/internal-calendars-panel";
import CalendarExportDialog from "@/components/calendar-export-dialog";
import { apiRequest, queryClient as globalQueryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { applyCalendarFilters } from "@/lib/calendar-filters";
import { resolveCalendarEventColor } from "@/lib/calendar-event-color";
import { useCalendarSourceFilter } from "@/hooks/useCalendarSourceFilter";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import type { CalendarEvent, CalendarStats, Goal, CalendarConnection, ExternalCalendar, ImportantDate } from "@shared/schema";

interface SharedCalendarEvent extends CalendarEvent {
  isSharedEvent: true;
}

interface SharedCalendarLayer {
  id: string;
  ownerId: string;
  externalCalendarId: string | null;
  permission: "view" | "edit";
  color: string | null;
  label: string | null;
  owner: { firstName: string | null; lastName: string | null; email: string | null };
  externalCalendar?: { name: string } | null;
  visibility?: { isVisible: boolean; customColor?: string | null };
}

const icalFormSchema = z.object({
  displayName: z.string().min(1, "Display name is required"),
  icalUrl: z.string()
    .min(1, "iCal URL is required")
    .refine(
      (url) => {
        // Accept webcal://, http://, or https:// protocols
        return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('webcal://');
      },
      { message: "Must be a valid URL (http://, https://, or webcal://)" }
    )
    .transform((url) => {
      // Convert webcal:// to https://
      if (url.startsWith('webcal://')) {
        return url.replace('webcal://', 'https://');
      }
      return url;
    }),
  color: z.string().default("#3b82f6"),
});

type ICalFormInput = z.input<typeof icalFormSchema>;
type ICalFormValues = z.output<typeof icalFormSchema>;

// Google Calendar Connection Response Types
interface GoogleCalendarAccountItem {
  connectionId: string;
  email?: string | null;
  lastSyncAt?: string | null;
  syncedEventCount?: number;
  calendars: Array<{
    id: string;
    externalCalendarId: string;
    name: string;
    color: string | null;
    isVisible: boolean;
    isPrimary: boolean;
  }>;
}

interface GoogleCalendarConnectionsResponse {
  connections: GoogleCalendarAccountItem[];
}

// Google Calendar Section Component for Two-Way Sync
function GoogleCalendarSection({ queryClient }: { queryClient: ReturnType<typeof useQueryClient> }) {
  const [hasAutoSynced, setHasAutoSynced] = useState(false);

  const { data: gcalData, isLoading: isLoadingGcal } = useQuery<GoogleCalendarConnectionsResponse>({
    queryKey: ["/api/google-calendar/connection"],
  });

  const gcalConnections = gcalData?.connections ?? [];
  const isConnected = gcalConnections.length > 0;

  const syncMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/google-calendar/sync");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/google-calendar/connection"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
    },
  });

  // Reset hasAutoSynced when all connections are removed
  useEffect(() => {
    if (!isConnected) {
      setHasAutoSynced(false);
    }
  }, [isConnected]);

  // Automatically sync when the page loads if any account needs syncing
  useEffect(() => {
    if (isConnected && !hasAutoSynced && !syncMutation.isPending) {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      const needsSync = gcalConnections.some(conn => {
        const lastSync = conn.lastSyncAt ? new Date(conn.lastSyncAt) : null;
        return !lastSync || lastSync < fiveMinutesAgo;
      });
      if (needsSync) {
        console.log("🗓️ Auto-syncing Google Calendar...");
        syncMutation.mutate();
      }
      setHasAutoSynced(true);
    }
  }, [isConnected, hasAutoSynced, syncMutation.isPending]);

  const disconnectMutation = useMutation({
    mutationFn: async (connectionId?: string) => {
      const url = connectionId
        ? `/api/google-calendar/disconnect?connectionId=${encodeURIComponent(connectionId)}`
        : "/api/google-calendar/disconnect";
      return await apiRequest("DELETE", url);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/google-calendar/connection"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
    },
  });

  const toggleCalendarMutation = useMutation({
    mutationFn: async ({ calendarId, isVisible }: { calendarId: string; isVisible: boolean }) => {
      return await apiRequest("PATCH", `/api/google-calendar/calendars/${calendarId}/visibility`, { isVisible });
    },
    onMutate: async ({ calendarId, isVisible }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/google-calendar/connection"] });
      const previousData = queryClient.getQueryData<GoogleCalendarConnectionsResponse>(["/api/google-calendar/connection"]);

      if (previousData?.connections) {
        queryClient.setQueryData<GoogleCalendarConnectionsResponse>(["/api/google-calendar/connection"], {
          ...previousData,
          connections: previousData.connections.map(conn => ({
            ...conn,
            calendars: conn.calendars.map(cal =>
              cal.id === calendarId ? { ...cal, isVisible } : cal
            ),
          })),
        });
      }

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["/api/google-calendar/connection"], context.previousData);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/google-calendar/connection"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
    },
  });

  const handleConnect = () => {
    window.location.href = "/api/google-calendar/connect";
  };

  if (isLoadingGcal) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Google Calendar (Two-Way Sync)</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Full two-way sync: events you create here appear in Google Calendar and vice versa
          </p>
        </div>
        {isConnected && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleConnect}
            className="shrink-0"
            data-testid="button-add-google-account"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Account
          </Button>
        )}
      </div>

      {isConnected ? (
        <div className="space-y-3">
          {gcalConnections.map((conn) => (
            <Card key={conn.connectionId}>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="font-medium">Connected</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{conn.email}</p>
                    {conn.lastSyncAt && (
                      <p className="text-xs text-muted-foreground">
                        Last synced: {formatDistanceToNow(new Date(conn.lastSyncAt), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                      data-testid="button-sync-google-calendar"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                      {syncMutation.isPending ? "Syncing..." : "Sync"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const impact =
                          typeof conn.syncedEventCount === "number"
                            ? conn.syncedEventCount === 1
                              ? "This will remove 1 synced event from GoalVia."
                              : `This will remove ${conn.syncedEventCount} synced events from GoalVia.`
                            : "Synced events from this account will be removed from GoalVia.";
                        // eslint-disable-next-line no-alert
                        if (window.confirm(`Disconnect Google Calendar${conn.email ? ` (${conn.email})` : ""}? ${impact}`)) {
                          disconnectMutation.mutate(conn.connectionId);
                        }
                      }}
                      disabled={disconnectMutation.isPending}
                      data-testid="button-disconnect-google-calendar"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>

                {conn.calendars && conn.calendars.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Calendars</p>
                      <p className="text-xs text-muted-foreground">Toggle calendars to show/hide their events</p>
                      {conn.calendars.map((calendar) => (
                        <div key={calendar.id} className="flex items-center justify-between p-2 rounded-lg border">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: calendar.color ?? '#3b82f6' }}
                            />
                            <span className="text-sm">
                              {calendar.name}
                              {calendar.isPrimary && (
                                <Badge variant="secondary" className="ml-2 text-xs">Primary</Badge>
                              )}
                            </span>
                          </div>
                          <Switch
                            checked={calendar.isVisible}
                            onCheckedChange={(checked) =>
                              toggleCalendarMutation.mutate({ calendarId: calendar.id, isVisible: checked })
                            }
                            data-testid={`switch-calendar-${calendar.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-muted/50">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900">
                <CalendarIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="font-medium">Connect Google Calendar</p>
                <p className="text-sm text-muted-foreground">
                  Enable two-way sync to create and edit events from either GoalVia or Google Calendar
                </p>
              </div>
            </div>
            <Button
              onClick={handleConnect}
              className="w-full sm:w-auto"
              data-testid="button-connect-google-calendar"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Connect Google Calendar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"month" | "week" | "day">("month");
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useQuickCreateRoute("event", () => setIsCreateModalOpen(true));
  const [selectedDateForCreation, setSelectedDateForCreation] = useState<Date | null>(null);
  const [createEventType, setCreateEventType] = useState<string>("event");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const { hiddenSources } = useCalendarSourceFilter();
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isImportantDatesOpen, setIsImportantDatesOpen] = useState(false);
  const [editingImportantDate, setEditingImportantDate] = useState<ImportantDate | null>(null);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // AI Calendar Assistant state
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState<Array<{role: 'user'|'assistant'; content: string; executed?: any[]}>>([]);
  const [aiInput, setAiInput] = useState("");
  const [aiIsLoading, setAiIsLoading] = useState(false);
  const [pendingConfirmMsg, setPendingConfirmMsg] = useState("");
  const aiInputRef = useRef<HTMLTextAreaElement>(null);
  const aiScrollRef = useRef<HTMLDivElement>(null);

  // Get start and end dates for current view
  const { startDate, endDate } = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (view === "month") {
      // Get first day of the month
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      
      // Extend to include overflow days from previous month
      // Go back to the start of the week (Sunday = 0)
      const firstDayOfWeek = start.getDay();
      start.setDate(start.getDate() - firstDayOfWeek);
      
      // Calendar shows 42 days (6 weeks), so set end to 41 days after start
      end.setTime(start.getTime());
      end.setDate(end.getDate() + 41);
      end.setHours(23, 59, 59, 999);
    } else if (view === "week") {
      const dayOfWeek = start.getDay();
      start.setDate(start.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    return { startDate: start, endDate: end };
  }, [currentDate, view]);

  const { data: events = [], isLoading } = useQuery<CalendarEvent[]>({
    queryKey: ["/api/calendar", startDate.toISOString(), endDate.toISOString(), eventTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      
      const response = await fetch(`/api/calendar?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch events');
      return response.json();
    },
  });

  // Fetch ALL events for export functionality (no date range filter)
  const { data: allEvents = [] } = useQuery<CalendarEvent[]>({
    queryKey: ["/api/calendar/all"],
    queryFn: async () => {
      const response = await fetch('/api/calendar');
      if (!response.ok) throw new Error('Failed to fetch all events');
      return response.json();
    },
    enabled: isExportDialogOpen, // Only fetch when export dialog is open
  });

  const { data: stats } = useQuery<CalendarStats>({
    queryKey: ["/api/stats/calendar", currentDate.getMonth(), currentDate.getFullYear()],
    queryFn: async () => {
      const params = new URLSearchParams({
        month: currentDate.getMonth().toString(),
        year: currentDate.getFullYear().toString(),
      });
      const response = await fetch(`/api/stats/calendar?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch calendar stats');
      return response.json();
    },
  });

  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: categories = [] } = useQuery<any[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: importantDates = [], isLoading: isLoadingImportantDates } = useQuery<ImportantDate[]>({
    queryKey: ["/api/important-dates"],
    enabled: isImportantDatesOpen,
  });

  const { data: connections = [], isLoading: isLoadingConnections } = useQuery<(CalendarConnection & { externalCalendars?: ExternalCalendar[] })[]>({
    queryKey: ["/api/calendar/connections"],
  });

  const { data: internalCalendars = [], isLoading: isLoadingInternalCalendars } = useQuery<InternalCalendar[]>({
    queryKey: INTERNAL_CALENDARS_QUERY_KEY,
  });

  const sharedEventsQueryUrl = useMemo(() => {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
    return `/api/calendar/events/shared?${params.toString()}`;
  }, [startDate, endDate]);

  const { data: sharedEvents = [] } = useQuery<SharedCalendarEvent[]>({
    queryKey: [sharedEventsQueryUrl],
  });
  const { data: sharedCalendarLayers = [] } = useQuery<SharedCalendarLayer[]>({
    queryKey: ["/api/calendar/shared-with-me"],
  });

  const setSharedLayerVisibility = useMutation({
    mutationFn: ({ id, isVisible }: { id: string; isVisible: boolean }) =>
      apiRequest("PATCH", `/api/calendar/shared/${id}/visibility`, { isVisible }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/shared-with-me"] });
      queryClient.invalidateQueries({ queryKey: [sharedEventsQueryUrl] });
    },
  });

  const internalCalendarsMap = useMemo(
    () => new Map(internalCalendars.map(calendar => [calendar.id, calendar])),
    [internalCalendars],
  );

  const visibleInternalCalendarIds = useMemo(() => {
    if (isLoadingInternalCalendars) return undefined;
    return new Set(internalCalendars.filter(calendar => calendar.isVisible).map(calendar => calendar.id));
  }, [internalCalendars, isLoadingInternalCalendars]);
  const defaultInternalCalendarId = useMemo(
    () => internalCalendars.find(calendar => calendar.isDefault)?.id ?? null,
    [internalCalendars],
  );

  // Build a map of external calendars by ID for quick lookup
  const externalCalendarsMap = useMemo(() => {
    const map = new Map<string, ExternalCalendar>();
    (connections ?? []).forEach(conn => {
      conn.externalCalendars?.forEach(cal => {
        map.set(cal.id, cal);
      });
    });
    return map;
  }, [connections]);

  // Build a set of visible external calendar IDs
  // Return undefined while loading to avoid hiding all external events
  const visibleCalendarIds = useMemo(() => {
    // If connections are still loading, return undefined to show all events
    if (isLoadingConnections) {
      return undefined;
    }
    
    const ids = new Set<string>();
    (connections ?? []).forEach(conn => {
      conn.externalCalendars?.forEach(cal => {
        if (cal.isVisible) {
          ids.add(cal.id);
        }
      });
    });
    return ids;
  }, [connections, isLoadingConnections]);

  const icalForm = useForm<ICalFormInput, any, ICalFormValues>({
    resolver: zodResolver(icalFormSchema),
    defaultValues: {
      displayName: "",
      icalUrl: "",
      color: "#3b82f6",
    },
  });

  // Important Dates Form
  const importantDateFormSchema = z.object({
    description: z.string().min(1, "Description is required"),
    eventDate: z.date({ error: "Date is required" }),
    isRecurring: z.boolean().default(false),
    recurrenceType: z.string().nullable().optional(),
    isGift: z.boolean().default(false),
    addToCalendar: z.boolean().default(false),
  });

  type ImportantDateFormInput = z.input<typeof importantDateFormSchema>;
  type ImportantDateFormValues = z.output<typeof importantDateFormSchema>;

  const importantDateForm = useForm<ImportantDateFormInput, any, ImportantDateFormValues>({
    resolver: zodResolver(importantDateFormSchema),
    defaultValues: {
      description: "",
      eventDate: new Date(),
      isRecurring: false,
      recurrenceType: null,
      isGift: false,
      addToCalendar: false,
    },
  });

  // Reset form when editing state changes
  useEffect(() => {
    if (editingImportantDate) {
      importantDateForm.reset({
        description: editingImportantDate.description,
        eventDate: new Date(editingImportantDate.eventDate),
        isRecurring: editingImportantDate.isRecurring,
        recurrenceType: editingImportantDate.recurrenceType,
        isGift: editingImportantDate.isGift,
        addToCalendar: editingImportantDate.addToCalendar,
      });
    } else {
      importantDateForm.reset({
        description: "",
        eventDate: new Date(),
        isRecurring: false,
        recurrenceType: null,
        isGift: false,
        addToCalendar: false,
      });
    }
  }, [editingImportantDate]);

  const createImportantDateMutation = useMutation({
    mutationFn: async (data: ImportantDateFormValues) => {
      return await apiRequest("POST", "/api/important-dates", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/important-dates"] });
      importantDateForm.reset();
    },
  });

  const updateImportantDateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ImportantDateFormValues> }) => {
      return await apiRequest("PATCH", `/api/important-dates/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/important-dates"] });
      setEditingImportantDate(null);
      importantDateForm.reset();
    },
  });

  const deleteImportantDateMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/important-dates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/important-dates"] });
    },
  });

  const handleImportantDateSubmit = (data: ImportantDateFormValues) => {
    if (editingImportantDate) {
      updateImportantDateMutation.mutate({ id: editingImportantDate.id, data });
    } else {
      createImportantDateMutation.mutate(data);
    }
  };

  const addICalMutation = useMutation({
    mutationFn: async (data: ICalFormValues) => {
      return await apiRequest("POST", "/api/calendar/connections/ical", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
      icalForm.reset();
    },
    onError: (error: Error) => {
    },
  });

  const refreshConnectionMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      const res = await apiRequest("POST", `/api/calendar/connections/${connectionId}/refresh`);
      return res.json() as Promise<{ success: boolean; eventCount: number; removedCount: number }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      if (data.removedCount > 0) {
        toast({
          title: "Calendar refreshed",
          description: `${data.removedCount} stale event${data.removedCount === 1 ? "" : "s"} removed.`,
        });
      }
    },
    onError: (error: Error) => {
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: async (connectionId: string) => {
      return await apiRequest("DELETE", `/api/calendar/connections/${connectionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
    },
    onError: (error: Error) => {
    },
  });

  const toggleCalendarVisibilityMutation = useMutation({
    mutationFn: async ({ calendarId, isVisible }: { calendarId: string; isVisible: boolean }) => {
      return await apiRequest("PATCH", `/api/calendar/calendars/${calendarId}`, { isVisible });
    },
    onMutate: async ({ calendarId, isVisible }) => {
      await queryClient.cancelQueries({ queryKey: ["/api/calendar/connections"] });
      const previousConnections = queryClient.getQueryData(["/api/calendar/connections"]);
      
      queryClient.setQueryData(["/api/calendar/connections"], (old: any[]) => {
        return old?.map(conn => ({
          ...conn,
          externalCalendars: conn.externalCalendars?.map((cal: ExternalCalendar) =>
            cal.id === calendarId ? { ...cal, isVisible } : cal
          ),
        }));
      });

      return { previousConnections };
    },
    onError: (error: Error, variables, context) => {
      if (context?.previousConnections) {
        queryClient.setQueryData(["/api/calendar/connections"], context.previousConnections);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar/connections"] });
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CalendarEvent> }) => {
      const response = await fetch(`/api/calendar/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update event');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
    },
  });

  const completeEventMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/calendar/${id}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error('Failed to complete event');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/calendar"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
    },
  });

  const allViewEvents = useMemo(
    () => [...events, ...sharedEvents],
    [events, sharedEvents],
  );

  // Apply all filters to current view events
  const filteredEvents = useMemo(() => {
    return applyCalendarFilters(allViewEvents as CalendarEvent[], {
      visibleInternalCalendarIds,
      defaultInternalCalendarId,
      visibleCalendarIds,
      eventTypeFilter,
      categoryFilter,
      hiddenSources,
    });
  }, [allViewEvents, visibleCalendarIds, visibleInternalCalendarIds, defaultInternalCalendarId, eventTypeFilter, categoryFilter, hiddenSources]);

  // Apply same filters to all events for export functionality
  const exportableEvents = useMemo(() => {
    return applyCalendarFilters(allEvents, {
      visibleInternalCalendarIds,
      defaultInternalCalendarId,
      visibleCalendarIds,
      eventTypeFilter,
      categoryFilter,
      hiddenSources,
    });
  }, [allEvents, visibleCalendarIds, visibleInternalCalendarIds, defaultInternalCalendarId, eventTypeFilter, categoryFilter, hiddenSources]);

  // Generate calendar days for month view
  const calendarDays = useMemo(() => {
    if (view !== "month") return [];

    const days = [];
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startCalendar = new Date(firstDay);
    startCalendar.setDate(startCalendar.getDate() - firstDay.getDay());

    for (let i = 0; i < 42; i++) {
      const date = new Date(startCalendar);
      date.setDate(startCalendar.getDate() + i);
      
      const dayEvents = filteredEvents.filter((event: CalendarEvent) => {
        const eventStart = new Date(event.startTime);
        const eventEnd = new Date(event.endTime);
        
        // For all-day events, compare dates properly
        if (event.allDay) {
          const dayYear = date.getFullYear();
          const dayMonth = date.getMonth();
          const dayDate = date.getDate();
          
          let startYear: number, startMonth: number, startDate: number;
          let endYear: number, endMonth: number, endDate: number;
          
          // External calendar events (iCal) use exclusive end dates at midnight UTC
          // Our own events store dates in local time with end at 23:59:59
          if (event.externalCalendarId) {
            // External events: use UTC dates
            startYear = eventStart.getUTCFullYear();
            startMonth = eventStart.getUTCMonth();
            startDate = eventStart.getUTCDate();
            endYear = eventEnd.getUTCFullYear();
            endMonth = eventEnd.getUTCMonth();
            endDate = eventEnd.getUTCDate();
            
            // Check if end time is exactly midnight UTC (exclusive end date from Google Calendar)
            if (eventEnd.getUTCHours() === 0 && eventEnd.getUTCMinutes() === 0 && eventEnd.getUTCSeconds() === 0) {
              // Subtract 1 day to get the actual last day
              const adjustedEnd = new Date(eventEnd.getTime() - 1);
              endYear = adjustedEnd.getUTCFullYear();
              endMonth = adjustedEnd.getUTCMonth();
              endDate = adjustedEnd.getUTCDate();
            }
          } else {
            // Our own events: use local dates (stored with local midnight/23:59:59)
            startYear = eventStart.getFullYear();
            startMonth = eventStart.getMonth();
            startDate = eventStart.getDate();
            endYear = eventEnd.getFullYear();
            endMonth = eventEnd.getMonth();
            endDate = eventEnd.getDate();
          }
          
          // Create comparable date values (YYYYMMDD as numbers)
          const dayValue = dayYear * 10000 + (dayMonth + 1) * 100 + dayDate;
          const startValue = startYear * 10000 + (startMonth + 1) * 100 + startDate;
          const endValue = endYear * 10000 + (endMonth + 1) * 100 + endDate;
          
          return dayValue >= startValue && dayValue <= endValue;
        }
        
        // For timed events, use local time comparison
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        
        const eventStartDay = new Date(eventStart);
        eventStartDay.setHours(0, 0, 0, 0);
        const eventEndDay = new Date(eventEnd);
        eventEndDay.setHours(0, 0, 0, 0);
        
        // Check if the day falls within the event's date range
        return dayStart >= eventStartDay && dayStart <= eventEndDay;
      });

      days.push({
        date,
        isCurrentMonth: date.getMonth() === currentDate.getMonth(),
        isToday: date.toDateString() === new Date().toDateString(),
        events: dayEvents,
      });
    }

    return days;
  }, [currentDate, view, filteredEvents]);

  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    
    if (view === "month") {
      newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1));
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7));
    } else {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1));
    }
    
    setCurrentDate(newDate);
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.isCompleted) return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100";
    
    // For external calendar events, don't use predefined colors
    if (event.externalCalendarId) {
      return "";
    }
    
    switch (event.eventType) {
      case "goal": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100";
      case "task": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100";
      case "reminder": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100";
    }
  };

  const getEventTypeIcon = (eventType: string) => {
    switch (eventType) {
      case "goal": return <Target className="h-3 w-3" />;
      case "task": return <CheckSquare className="h-3 w-3" />;
      case "checklist": return <CheckSquare className="h-3 w-3" />;
      default: return <CalendarIcon className="h-3 w-3" />;
    }
  };

  // All events — including checklist-backed ones — open the details dialog,
  // which offers "Open checklist" for checklist events plus recurring-series
  // delete options (with Google Calendar cleanup).
  const handleEventOpen = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Generate time slots for week/day view
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let i = 0; i < 24; i++) {
      slots.push({
        hour: i,
        label: new Date(0, 0, 0, i).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      });
    }
    return slots;
  }, []);

  // Generate week days for week view
  const weekDays = useMemo(() => {
    if (view !== "week") return [];
    
    const days = [];
    const start = new Date(startDate);
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    
    return days;
  }, [view, startDate]);

  // Get events for a specific day and hour
  const getEventsForSlot = (date: Date, hour: number) => {
    return filteredEvents.filter((event: CalendarEvent) => {
      if (event.allDay) return false; // Handle all-day events separately
      
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      const slotStart = new Date(date);
      slotStart.setHours(hour, 0, 0, 0);
      const slotEnd = new Date(date);
      slotEnd.setHours(hour + 1, 0, 0, 0);
      
      // Check if event overlaps with this time slot
      return (
        eventStart.toDateString() === date.toDateString() &&
        eventStart.getHours() === hour
      );
    });
  };

  // Get all-day events for a specific date
  const getAllDayEvents = (date: Date) => {
    return filteredEvents.filter((event: CalendarEvent) => {
      if (!event.allDay) return false;
      
      const eventStart = new Date(event.startTime);
      const eventEnd = new Date(event.endTime);
      
      // For all-day events, compare using UTC dates to avoid timezone shifts
      // This ensures a holiday on "2026-02-14" shows on Feb 14 regardless of timezone
      const dayYear = date.getFullYear();
      const dayMonth = date.getMonth();
      const dayDate = date.getDate();
      
      // Get the UTC date components from the event
      const startYear = eventStart.getUTCFullYear();
      const startMonth = eventStart.getUTCMonth();
      const startDate = eventStart.getUTCDate();
      
      // For end date: if it's at midnight UTC (00:00:00), it's an exclusive end date
      // (e.g., Feb 15 00:00:00 means the event ends at the END of Feb 14)
      // So we subtract 1 day to get the actual last day of the event
      let endYear = eventEnd.getUTCFullYear();
      let endMonth = eventEnd.getUTCMonth();
      let endDate = eventEnd.getUTCDate();
      
      // Check if end time is exactly midnight UTC (exclusive end date from Google Calendar)
      if (eventEnd.getUTCHours() === 0 && eventEnd.getUTCMinutes() === 0 && eventEnd.getUTCSeconds() === 0) {
        // Subtract 1 day to get the actual last day
        const adjustedEnd = new Date(eventEnd.getTime() - 1);
        endYear = adjustedEnd.getUTCFullYear();
        endMonth = adjustedEnd.getUTCMonth();
        endDate = adjustedEnd.getUTCDate();
      }
      
      // Create comparable date values (YYYYMMDD as numbers)
      const dayValue = dayYear * 10000 + (dayMonth + 1) * 100 + dayDate;
      const startValue = startYear * 10000 + (startMonth + 1) * 100 + startDate;
      const endValue = endYear * 10000 + (endMonth + 1) * 100 + endDate;
      
      // Check if the day falls within the event's date range (inclusive)
      return dayValue >= startValue && dayValue <= endValue;
    });
  };

  // Calculate event position and height based on time
  const getEventStyle = (event: CalendarEvent): React.CSSProperties => {
    const baseStyle = getEventStyleColor(event);
    
    if (event.allDay || view === "month") {
      return baseStyle;
    }

    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    const minutes = eventStart.getMinutes();
    const duration = (eventEnd.getTime() - eventStart.getTime()) / (1000 * 60); // duration in minutes
    
    return {
      ...baseStyle,
      marginTop: `${(minutes / 60) * 100}%`,
      height: `${Math.max((duration / 60) * 60, 30)}px`,
      position: 'absolute',
      width: 'calc(100% - 4px)',
      left: '2px',
    };
  };

  const getEventStyleColor = (event: CalendarEvent): React.CSSProperties => {
    const color = resolveCalendarEventColor(event, internalCalendarsMap, externalCalendarsMap);
    
    return {
      backgroundColor: `${color}20`,
      borderLeft: `3px solid ${color}`,
      color: color,
      // Completed scheduled checklists are visibly "done" on the calendar.
      ...(event.eventType === "checklist" && event.isCompleted
        ? { textDecoration: "line-through", opacity: 0.6 }
        : {}),
    };
  };

  const handleEventDragStart = (event: CalendarEvent) => {
    setDraggedEvent(event);
  };

  const handleEventDrop = (targetDate: Date) => {
    if (!draggedEvent) return;

    const timeDiff = new Date(draggedEvent.endTime).getTime() - new Date(draggedEvent.startTime).getTime();
    const newStartTime = new Date(targetDate);
    newStartTime.setHours(new Date(draggedEvent.startTime).getHours());
    newStartTime.setMinutes(new Date(draggedEvent.startTime).getMinutes());
    
    const newEndTime = new Date(newStartTime.getTime() + timeDiff);

    updateEventMutation.mutate({
      id: draggedEvent.id,
      updates: {
        startTime: newStartTime,
        endTime: newEndTime,
      },
    });

    setDraggedEvent(null);
  };

  const handleCompleteEvent = (event: CalendarEvent) => {
    completeEventMutation.mutate(event.id);
  };

  const handleDayClick = (date: Date) => {
    setSelectedDateForCreation(date);
    setCreateEventType("event");
    setIsCreateModalOpen(true);
  };

  const handleNewTaskClick = () => {
    setSelectedDateForCreation(new Date());
    setCreateEventType("task");
    setIsCreateModalOpen(true);
  };

  const handleNewEventClick = () => {
    setSelectedDateForCreation(new Date());
    setCreateEventType("event");
    setIsCreateModalOpen(true);
  };

  const sendAiMessage = async (overrideContent?: string, confirmedAction?: boolean) => {
    const messageText = (overrideContent ?? aiInput).trim();
    if (!messageText || aiIsLoading) return;
    const newMsg = { role: 'user' as const, content: messageText };
    const history = [...aiMessages, newMsg];
    setAiMessages(history);
    setAiInput("");
    setAiIsLoading(true);
    requestAnimationFrame(() => aiInputRef.current?.focus());
    try {
      const resp = await apiRequest("POST", "/api/calendar/ai-chat", {
        messages: history.map(m => ({ role: m.role, content: m.content })),
        confirmedAction: confirmedAction ?? false,
      });
      const data = await resp.json();
      if (data.needsConfirmation) {
        setPendingConfirmMsg(data.confirmationMessage ?? data.reply ?? "");
      } else {
        setPendingConfirmMsg("");
      }
      setAiMessages(prev => [...prev, {
        role: 'assistant',
        content: data.reply ?? "Done!",
        executed: data.executed ?? [],
      }]);
      if (data.executed?.some((e: any) => ["created","updated","deleted","bulk_deleted","bulk_updated"].includes(e.type))) {
        queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      }
    } catch {
      setAiMessages(prev => [...prev, { role: 'assistant', content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setAiIsLoading(false);
      requestAnimationFrame(() => aiInputRef.current?.focus());
    }
  };

  useEffect(() => {
    if (isAiChatOpen) {
      setTimeout(() => aiInputRef.current?.focus(), 150);
    }
  }, [isAiChatOpen]);

  useEffect(() => {
    if (aiScrollRef.current) {
      aiScrollRef.current.scrollTop = aiScrollRef.current.scrollHeight;
    }
  }, [aiMessages]);

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <CalendarIcon className="h-6 w-6" />
                Calendar
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Manage your events and schedule
              </p>
            </div>
              
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateDate("prev")}
                data-testid="button-prev-date"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateDate("next")}
                data-testid="button-next-date"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h2 className="text-lg sm:text-xl font-semibold">
              {currentDate.toLocaleString("default", { 
                month: "long", 
                year: "numeric",
                ...(view === "day" && { day: "numeric" })
              })}
            </h2>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
              {/* View Toggle */}
              <Select value={view} onValueChange={(value: "month" | "week" | "day") => setView(value)}>
                <SelectTrigger className="w-full sm:w-32" data-testid="select-calendar-view">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                  <SelectItem value="day">Day</SelectItem>
                </SelectContent>
              </Select>

              {/* Type Filter */}
              <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
                <SelectTrigger className="w-full sm:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="event">Events</SelectItem>
                  <SelectItem value="goal">Goals</SelectItem>
                  <SelectItem value="task">Tasks</SelectItem>
                  <SelectItem value="reminder">Reminders</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Filter (Multi-Select) */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full sm:w-40 justify-between"
                    data-testid="button-category-filter"
                  >
                    <span className="truncate">
                      {categoryFilter.length > 0
                        ? `${categoryFilter.length} ${categoryFilter.length === 1 ? 'Category' : 'Categories'}`
                        : 'All Categories'}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[200px] p-0" align="start">
                  <div className="p-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Categories</span>
                      {categoryFilter.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-1 text-xs"
                          onClick={() => setCategoryFilter([])}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {categories.map((category: any) => (
                        <div
                          key={category.id}
                          className="flex items-center space-x-2 p-1 hover:bg-accent rounded cursor-pointer"
                          onClick={() => {
                            const newCategories = categoryFilter.includes(category.name)
                              ? categoryFilter.filter(c => c !== category.name)
                              : [...categoryFilter, category.name];
                            setCategoryFilter(newCategories);
                          }}
                        >
                          <Checkbox
                            checked={categoryFilter.includes(category.name)}
                            onCheckedChange={() => {}}
                            data-testid={`checkbox-category-${category.name}`}
                          />
                          <span className="text-sm flex-1">{category.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                onClick={handleNewTaskClick}
                variant="outline"
                size="sm"
                className="inline-flex"
                data-testid="button-create-task"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Task</span>
              </Button>
              <Button
                onClick={handleNewEventClick}
                size="sm"
                className="w-full sm:w-auto"
                data-testid="button-create-event"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Event
              </Button>
              <Button
                onClick={() => setIsExportDialogOpen(true)}
                variant="outline"
                size="sm"
                className="inline-flex"
                data-testid="button-export-pdf"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Export PDF</span>
              </Button>
              <Button
                onClick={() => setIsAiChatOpen(prev => !prev)}
                variant={isAiChatOpen ? "default" : "outline"}
                size="sm"
                className="inline-flex"
                data-testid="button-calendar-ai"
              >
                <Sparkles className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">AI</span>
              </Button>
              <Button
                onClick={() => setIsImportantDatesOpen(true)}
                variant="outline"
                size="sm"
                className="inline-flex"
                data-testid="button-important-dates"
              >
                <Star className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Important Dates</span>
              </Button>
              <Button
                onClick={() => {
                  window.location.href = "/api/google-calendar/connect";
                }}
                variant="outline"
                size="sm"
                className="inline-flex"
                aria-label="Connect Google Calendar"
                title="Connect Google Calendar"
                data-testid="button-connect-google-calendar-page"
              >
                <CalendarIcon className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Google Calendar</span>
              </Button>
              <Button
                onClick={() => {
                  setIsSettingsOpen(true);
                  window.setTimeout(() => {
                    document
                      .querySelector('[data-testid="calendar-outlook-connection-settings"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 100);
                }}
                variant="outline"
                size="sm"
                className="inline-flex"
                aria-label="Connect Outlook Calendar"
                title="Connect Outlook Calendar"
                data-testid="button-connect-outlook-calendar-page"
              >
                <ExternalLink className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Outlook Calendar</span>
              </Button>
              <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="inline-flex" data-testid="button-calendar-settings">
                    <Settings className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Settings</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Calendar Settings</DialogTitle>
                  </DialogHeader>

                  <div className="space-y-6 mt-4">
                    {/* Section: Google Calendar Two-Way Sync */}
                    <GoogleCalendarSection queryClient={queryClient} />

                    <Separator />

                    {/* These are recipient-local overlays: hiding a layer never
                        changes the owner's calendar or another recipient's view. */}
                    <div className="space-y-3" data-testid="shared-calendar-layers">
                      <div>
                        <h3 className="text-lg font-semibold">Shared calendars</h3>
                        <p className="text-sm text-muted-foreground">Calendars other people have shared with you.</p>
                      </div>
                      {sharedCalendarLayers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No shared calendars.</p>
                      ) : sharedCalendarLayers.map(layer => {
                        const ownerName = [layer.owner.firstName, layer.owner.lastName].filter(Boolean).join(" ") || layer.owner.email || "Shared calendar";
                        const visible = layer.visibility?.isVisible !== false;
                        return (
                          <div key={layer.id} className="flex items-center justify-between rounded-md border p-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: layer.visibility?.customColor || layer.color || "#64748b" }} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{layer.label || layer.externalCalendar?.name || `${ownerName}'s internal calendar`}</p>
                                <p className="text-xs text-muted-foreground">{ownerName} · {layer.permission}</p>
                              </div>
                            </div>
                            <Switch
                              checked={visible}
                              onCheckedChange={isVisible => setSharedLayerVisibility.mutate({ id: layer.id, isVisible })}
                              aria-label={`Show ${layer.label || ownerName}`}
                              data-testid={`toggle-shared-${layer.id}`}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <Separator />

                    {/* Section A: Add iCal Feed Form */}
                    <div className="space-y-4" data-testid="calendar-outlook-connection-settings">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">Connect External Calendar (Read-Only)</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            Add Outlook, Apple, or any calendar with an iCal feed (one-way sync)
                          </p>
                        </div>
                      </div>
                      
                      {/* Connection Instructions */}
                      <Card className="bg-muted/50">
                        <CardContent className="pt-4 pb-4">
                          <div className="space-y-4">
                            {/* Outlook/Office 365 */}
                            <div className="space-y-2">
                              <h4 className="font-medium text-sm flex items-center gap-2">
                                <ExternalLink className="h-4 w-4" />
                                Outlook / Office 365
                              </h4>
                              <ol className="text-sm text-muted-foreground space-y-1 ml-6 list-decimal">
                                <li>Open <a href="https://outlook.office.com/calendar" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Outlook Calendar</a></li>
                                <li>Click Settings (gear) → View all Outlook settings</li>
                                <li>Go to Calendar → Shared calendars</li>
                                <li>Under "Publish a calendar", select your calendar and click "Publish"</li>
                                <li>Copy the ICS link and paste it below</li>
                              </ol>
                            </div>

                            <Separator />

                            {/* Apple iCloud */}
                            <div className="space-y-2">
                              <h4 className="font-medium text-sm flex items-center gap-2">
                                <ExternalLink className="h-4 w-4" />
                                Apple iCloud Calendar
                              </h4>
                              <ol className="text-sm text-muted-foreground space-y-1 ml-6 list-decimal">
                                <li>Open <a href="https://www.icloud.com/calendar" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">iCloud Calendar</a></li>
                                <li>Click the share icon next to your calendar</li>
                                <li>Enable "Public Calendar"</li>
                                <li>Copy the webcal:// or https:// URL</li>
                                <li>Paste the URL below (webcal:// URLs will be automatically converted)</li>
                              </ol>
                            </div>

                            <p className="text-xs text-muted-foreground mt-3">
                              💡 <strong>Tip:</strong> Your calendar will sync automatically. Events update every few hours.
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      <Form {...icalForm}>
                        <form onSubmit={icalForm.handleSubmit((data) => addICalMutation.mutate(data))} className="space-y-4">
                          <FormField
                            control={icalForm.control}
                            name="displayName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Display Name</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="My Calendar" 
                                    data-testid="input-ical-name"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={icalForm.control}
                            name="icalUrl"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>iCal URL</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    placeholder="https://... or webcal://..." 
                                    data-testid="input-ical-url"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={icalForm.control}
                            name="color"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Calendar Color (optional)</FormLabel>
                                <FormControl>
                                  <Input 
                                    {...field} 
                                    type="color" 
                                    className="h-10 w-20"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button 
                            type="submit" 
                            disabled={addICalMutation.isPending}
                            data-testid="button-add-ical"
                          >
                            {addICalMutation.isPending ? "Adding..." : "Add iCal Feed"}
                          </Button>
                        </form>
                      </Form>
                    </div>

                    <Separator />

                    {/* Section B: Connected Calendars List */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Connected Calendars</h3>
                      {isLoadingConnections ? (
                        <div className="text-sm text-muted-foreground">Loading connections...</div>
                      ) : connections.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No calendar connections yet</div>
                      ) : (
                        <div className="space-y-3">
                          {connections.map((connection) => (
                            <Card key={connection.id}>
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-medium">{connection.displayName}</h4>
                                      <Badge variant="outline">{connection.provider}</Badge>
                                    </div>
                                    {connection.lastSyncAt && (
                                      <p className="text-sm text-muted-foreground">
                                        Last synced {formatDistanceToNow(new Date(connection.lastSyncAt), { addSuffix: true })}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => refreshConnectionMutation.mutate(connection.id)}
                                      disabled={refreshConnectionMutation.isPending}
                                      data-testid={`button-refresh-${connection.id}`}
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deleteConnectionMutation.mutate(connection.id)}
                                      disabled={deleteConnectionMutation.isPending}
                                      data-testid={`button-delete-${connection.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator />

                    {/* Section C: Calendar Visibility Toggles */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Calendar Visibility</h3>
                      {connections.some(c => c.externalCalendars && c.externalCalendars.length > 0) ? (
                        <div className="space-y-3">
                          {connections.map((connection) => (
                            connection.externalCalendars && connection.externalCalendars.length > 0 && (
                              <div key={connection.id} className="space-y-2">
                                <p className="text-sm font-medium text-muted-foreground">{connection.displayName}</p>
                                {connection.externalCalendars.map((calendar) => (
                                  <div key={calendar.id} className="flex items-center justify-between p-3 rounded-lg border">
                                    <div className="flex items-center gap-3">
                                      <div 
                                        className="w-4 h-4 rounded" 
                                        style={{ backgroundColor: calendar.color || "#3b82f6" }}
                                      />
                                      <span className="text-sm">{calendar.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={calendar.isVisible}
                                        onCheckedChange={(checked) =>
                                          toggleCalendarVisibilityMutation.mutate({
                                            calendarId: calendar.id,
                                            isVisible: checked
                                          })
                                        }
                                        data-testid={`toggle-calendar-${calendar.id}`}
                                        disabled={toggleCalendarVisibilityMutation.isPending}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          No external calendars found. Add an iCal feed to see calendars here.
                        </div>
                      )}
                    </div>

                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-lg font-bold text-primary">{stats.totalEvents}</div>
                  <div className="text-xs text-muted-foreground">Total Events</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-lg font-bold text-blue-600">{stats.upcomingEvents}</div>
                  <div className="text-xs text-muted-foreground">Upcoming</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-lg font-bold text-green-600">{stats.completedEvents}</div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-lg font-bold text-purple-600">{stats.goalEvents}</div>
                  <div className="text-xs text-muted-foreground">Goal Events</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <div className="text-lg font-bold text-orange-600">{stats.taskEvents}</div>
                  <div className="text-xs text-muted-foreground">Task Events</div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Calendar Content */}
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="flex-1 p-6 overflow-auto">
          {(isLoading || isLoadingConnections) ? (
            <div className="space-y-4">
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 42 }).map((_, i) => (
                  <Skeleton key={i} className="h-32" />
                ))}
              </div>
            </div>
          ) : view === "month" && (
            <div className="grid grid-cols-7 gap-1 h-full">
              {/* Header */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="p-2 text-center font-semibold text-muted-foreground border-b">
                  {day}
                </div>
              ))}
              
              {/* Calendar Days */}
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  className={`p-2 border border-border min-h-[120px] cursor-pointer hover:bg-muted/50 transition-colors ${
                    !day.isCurrentMonth ? "bg-muted/30" : ""
                  } ${
                    day.isToday ? "bg-primary/10" : ""
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleEventDrop(day.date)}
                  onClick={() => handleDayClick(day.date)}
                  data-testid={`calendar-day-${day.date.toISOString().split('T')[0]}`}
                >
                  <div className={`text-sm ${
                    day.isCurrentMonth ? "text-foreground" : "text-muted-foreground"
                  }`}>
                    {day.date.getDate()}
                  </div>
                  
                  <div className="space-y-1 mt-1">
                    {day.events.slice(0, 3).map((event: CalendarEvent) => (
                      <div
                        key={event.id}
                        className={`text-xs p-1 rounded cursor-pointer ${getEventColor(event)}`}
                        style={getEventStyle(event)}
                        draggable
                        onDragStart={() => handleEventDragStart(event)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEventOpen(event);
                        }}
                        data-testid={`event-${event.id}`}
                      >
                        <div className="flex items-center gap-1">
                          {getEventTypeIcon(event.eventType)}
                          <span className="truncate">{event.title}</span>
                          {event.externalCalendarId && (
                            <ExternalLink className="h-2.5 w-2.5 opacity-60 flex-shrink-0" />
                          )}
                          {!event.allDay && (
                            <span className="text-xs opacity-70">
                              {formatTime(event.startTime)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {day.events.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{day.events.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Week View - Grid with time slots */}
          {view === "week" && (
            <div className="overflow-auto">
              <div className="min-w-[800px]">
                {/* Week header */}
                <div className="grid grid-cols-8 border-b sticky top-0 bg-background z-10">
                  <div className="p-2 border-r"></div>
                  {weekDays.map((day, index) => (
                    <div 
                      key={index} 
                      className={`p-2 text-center font-semibold border-r ${
                        day.toDateString() === new Date().toDateString() ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="text-xs text-muted-foreground">
                        {day.toLocaleDateString([], { weekday: 'short' })}
                      </div>
                      <div className={`text-lg ${
                        day.toDateString() === new Date().toDateString() ? 'text-primary font-bold' : ''
                      }`}>
                        {day.getDate()}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* All-day events row */}
                <div className="grid grid-cols-8 border-b bg-muted/30">
                  <div className="p-2 border-r text-xs text-muted-foreground">All day</div>
                  {weekDays.map((day, index) => (
                    <div key={index} className="p-1 border-r min-h-[40px]">
                      {getAllDayEvents(day).map((event: CalendarEvent) => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 mb-1 rounded cursor-pointer ${getEventColor(event)}`}
                          style={getEventStyleColor(event)}
                          onClick={() => handleEventOpen(event)}
                          data-testid={`event-${event.id}`}
                        >
                          <span className="truncate block">{event.title}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Time slots */}
                {timeSlots.map((slot) => (
                  <div key={slot.hour} className="grid grid-cols-8 border-b">
                    <div className="p-2 border-r text-xs text-muted-foreground text-right">
                      {slot.label}
                    </div>
                    {weekDays.map((day, dayIndex) => (
                      <div 
                        key={dayIndex} 
                        className="border-r min-h-[60px] relative hover:bg-muted/50 cursor-pointer"
                        onClick={() => {
                          const clickedDate = new Date(day);
                          clickedDate.setHours(slot.hour, 0, 0, 0);
                          handleDayClick(clickedDate);
                        }}
                      >
                        {getEventsForSlot(day, slot.hour).map((event: CalendarEvent) => (
                          <div
                            key={event.id}
                            className={`text-xs p-1 rounded cursor-pointer overflow-hidden ${getEventColor(event)}`}
                            style={getEventStyle(event)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventOpen(event);
                            }}
                            data-testid={`event-${event.id}`}
                          >
                            <div className="font-semibold truncate">{event.title}</div>
                            <div className="text-xs opacity-70">
                              {formatTime(event.startTime)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Day View - Single column with time slots */}
          {view === "day" && (
            <div className="max-w-2xl mx-auto">
              {/* All-day events */}
              {getAllDayEvents(currentDate).length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-semibold text-muted-foreground mb-2">All day</div>
                  <div className="space-y-2">
                    {getAllDayEvents(currentDate).map((event: CalendarEvent) => (
                      <Card
                        key={event.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        style={getEventStyleColor(event)}
                        onClick={() => handleEventOpen(event)}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            {getEventTypeIcon(event.eventType)}
                            <span className="font-semibold">{event.title}</span>
                            {event.externalCalendarId && (
                              <ExternalLink className="h-3 w-3 opacity-60" />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Time slots */}
              <div className="border rounded-lg overflow-hidden">
                {timeSlots.map((slot) => (
                  <div key={slot.hour} className="flex border-b last:border-b-0">
                    <div className="w-20 p-2 border-r text-xs text-muted-foreground text-right bg-muted/30">
                      {slot.label}
                    </div>
                    <div 
                      className="flex-1 min-h-[60px] relative hover:bg-muted/50 cursor-pointer"
                      onClick={() => {
                        const clickedDate = new Date(currentDate);
                        clickedDate.setHours(slot.hour, 0, 0, 0);
                        handleDayClick(clickedDate);
                      }}
                    >
                      {getEventsForSlot(currentDate, slot.hour).map((event: CalendarEvent) => (
                        <div
                          key={event.id}
                          className={`p-2 rounded cursor-pointer overflow-hidden ${getEventColor(event)}`}
                          style={getEventStyle(event)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventOpen(event);
                          }}
                          data-testid={`event-${event.id}`}
                        >
                          <div className="font-semibold">{event.title}</div>
                          <div className="text-xs opacity-70">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </div>
                          {event.location && (
                            <div className="text-xs opacity-70 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {event.location}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setSelectedDateForCreation(null);
          setCreateEventType("event");
        }}
        goals={goals}
        selectedDate={selectedDateForCreation}
        defaultEventType={createEventType}
      />

      {/* Event Details Dialog */}
      <EventDetailsDialog
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        goals={goals}
      />

      {/* Calendar Export Dialog */}
      <CalendarExportDialog
        open={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        events={exportableEvents}
      />

      {/* Important Dates Dialog */}
      <Dialog 
        open={isImportantDatesOpen} 
        onOpenChange={(open) => {
          setIsImportantDatesOpen(open);
          if (!open) {
            setEditingImportantDate(null);
            importantDateForm.reset();
          }
        }}
      >
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-important-dates">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Important Dates
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            {/* Form Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {editingImportantDate ? "Edit Important Date" : "Add New Important Date"}
              </h3>
              
              <Form {...importantDateForm}>
                <form onSubmit={importantDateForm.handleSubmit(handleImportantDateSubmit)} className="space-y-4">
                  <FormField
                    control={importantDateForm.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description *</FormLabel>
                        <FormControl>
                          <Textarea 
                            {...field} 
                            placeholder="e.g., Mom's Birthday, Wedding Anniversary, Tax Filing Deadline..."
                            rows={3}
                            data-testid="input-important-date-description"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={importantDateForm.control}
                    name="eventDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Date *</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal"
                                data-testid="button-important-date-picker"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? format(field.value, "PPP") : "Pick a date"}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <DatePickerCalendar
                                mode="single"
                                selected={field.value}
                                onSelect={(date) => {
                                  if (date) {
                                    field.onChange(date);
                                  }
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={importantDateForm.control}
                    name="isRecurring"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="flex items-center gap-2">
                            <Repeat className="h-4 w-4" />
                            Recurring Event
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            This event repeats on a schedule
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-important-date-recurring"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {importantDateForm.watch("isRecurring") && (
                    <FormField
                      control={importantDateForm.control}
                      name="recurrenceType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Recurrence Type</FormLabel>
                          <Select 
                            value={field.value || ""} 
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="select-important-date-recurrence-type">
                                <SelectValue placeholder="Select frequency" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="weekly">Weekly</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="annually">Annually</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={importantDateForm.control}
                    name="isGift"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="flex items-center gap-2">
                            <Gift className="h-4 w-4" />
                            Gift Reminder
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Remind me to get a gift
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-important-date-gift"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={importantDateForm.control}
                    name="addToCalendar"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4" />
                            Add to Calendar
                          </FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Show this event on my calendar
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="switch-important-date-calendar"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="submit" 
                      disabled={createImportantDateMutation.isPending || updateImportantDateMutation.isPending}
                      data-testid="button-save-important-date"
                    >
                      {(createImportantDateMutation.isPending || updateImportantDateMutation.isPending) 
                        ? "Saving..." 
                        : editingImportantDate 
                          ? "Update" 
                          : "Add Important Date"}
                    </Button>
                    {editingImportantDate && (
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => {
                          setEditingImportantDate(null);
                          importantDateForm.reset();
                        }}
                        data-testid="button-cancel-edit-important-date"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </div>

            {/* List Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Your Important Dates</h3>
              
              {isLoadingImportantDates ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : importantDates.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    <Star className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No important dates yet</p>
                    <p className="text-sm">Add birthdays, anniversaries, and other special events!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                  {importantDates.map((date) => (
                    <Card key={date.id} className="relative" data-testid={`card-important-date-${date.id}`}>
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium" data-testid={`text-important-date-description-${date.id}`}>
                              {date.description}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {format(new Date(date.eventDate), "PPPP")}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {date.isRecurring && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <Repeat className="h-3 w-3" />
                                  {date.recurrenceType || "Recurring"}
                                </Badge>
                              )}
                              {date.isGift && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <Gift className="h-3 w-3" />
                                  Gift
                                </Badge>
                              )}
                              {date.addToCalendar && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  On Calendar
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingImportantDate(date)}
                              data-testid={`button-edit-important-date-${date.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteImportantDateMutation.mutate(date.id)}
                              disabled={deleteImportantDateMutation.isPending}
                              data-testid={`button-delete-important-date-${date.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── AI Calendar Assistant Panel ─────────────────────────────────────── */}
      {isAiChatOpen && (
        <div className="fixed right-0 top-0 h-full w-[400px] bg-background border-l shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-950/30 dark:to-indigo-950/30">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <h2 className="font-semibold text-sm">Calendar AI</h2>
                <p className="text-xs text-muted-foreground">Powered by AI</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsAiChatOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Messages */}
          <div ref={aiScrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
            {aiMessages.length === 0 ? (
              <div className="text-center py-4">
                <div className="h-12 w-12 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="font-semibold text-sm mb-1">Calendar AI Assistant</h3>
                <p className="text-xs text-muted-foreground mb-4">Schedule appointments, find free time, or manage events using natural language.</p>
                <div className="space-y-2 text-left">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-1">Try asking:</p>
                  {[
                    "Find the best time for a 1.5hr hair appointment next week",
                    "Schedule a doctor appointment Tuesday morning",
                    "What does my schedule look like this week?",
                    "Remove all my recurring Tuesday 9am meetings",
                    "Move all my Monday meetings back 30 minutes",
                  ].map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => sendAiMessage(prompt)}
                      className="w-full text-left text-xs p-2.5 rounded-lg border hover:bg-muted transition-colors"
                    >
                      "{prompt}"
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              aiMessages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="h-7 w-7 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center shrink-0 mt-0.5">
                      <Bot className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                    </div>
                  )}
                  <div className={`max-w-[88%] ${msg.role === 'user' ? '' : 'space-y-2'}`}>
                    <div className={`rounded-2xl px-3.5 py-2.5 text-sm ${msg.role === 'user' ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-muted rounded-tl-sm'}`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    </div>
                    {/* Action result badges */}
                    {msg.executed && msg.executed.length > 0 && (
                      <div className="space-y-1.5 ml-0.5">
                        {msg.executed.map((ex: any, ei: number) => (
                          <div key={ei}>
                            {ex.type === 'created' && (
                              <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/50 rounded-lg px-2.5 py-1.5 border border-green-200 dark:border-green-800">
                                <span>✓</span>
                                <span>Created: <span className="font-medium">{ex.event?.title}</span></span>
                              </div>
                            )}
                            {ex.type === 'updated' && (
                              <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 rounded-lg px-2.5 py-1.5 border border-blue-200 dark:border-blue-800">
                                <span>✓</span>
                                <span>Updated: <span className="font-medium">{ex.event?.title}</span></span>
                              </div>
                            )}
                            {ex.type === 'deleted' && (
                              <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/50 rounded-lg px-2.5 py-1.5 border border-red-200 dark:border-red-800">
                                <span>✓</span>
                                <span>Event deleted</span>
                              </div>
                            )}
                            {ex.type === 'bulk_deleted' && (
                              <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/50 rounded-lg px-2.5 py-1.5 border border-red-200 dark:border-red-800">
                                <span>✓</span>
                                <span>{ex.count} event{ex.count !== 1 ? 's' : ''} deleted</span>
                              </div>
                            )}
                            {ex.type === 'bulk_updated' && (
                              <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 rounded-lg px-2.5 py-1.5 border border-blue-200 dark:border-blue-800">
                                <span>✓</span>
                                <span>{ex.count} event{ex.count !== 1 ? 's' : ''} updated</span>
                              </div>
                            )}
                            {ex.type === 'suggested_slots' && Array.isArray(ex.slots) && ex.slots.length > 0 && (
                              <div className="space-y-1.5">
                                <p className="text-xs font-medium text-muted-foreground">Available slots — click to book:</p>
                                {ex.slots.map((slot: any, si: number) => (
                                  <button
                                    key={si}
                                    onClick={() => sendAiMessage(`Book option ${si + 1}: ${slot.label}`)}
                                    className="w-full text-left text-xs p-2 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/50 hover:bg-violet-100 dark:hover:bg-violet-900/50 transition-colors flex items-center gap-2"
                                  >
                                    <span className="h-5 w-5 rounded-full bg-violet-600 text-white flex items-center justify-center text-xs font-bold shrink-0">{si + 1}</span>
                                    <span className="font-medium">{slot.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            {aiIsLoading && (
              <div className="flex gap-2 justify-start">
                <div className="h-7 w-7 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center shrink-0">
                  <Loader2 className="h-4 w-4 text-violet-600 animate-spin" />
                </div>
                <div className="bg-muted rounded-2xl rounded-tl-sm px-3.5 py-2.5">
                  <div className="flex gap-1 items-center h-4">
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Confirmation prompt */}
          {pendingConfirmMsg && (
            <div className="px-4 pb-2">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1">⚠️ Confirmation Required</p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mb-3 leading-relaxed">{pendingConfirmMsg}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => { setPendingConfirmMsg(""); sendAiMessage("Yes, confirmed, go ahead", true); }}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setPendingConfirmMsg("")}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2 items-end">
              <Textarea
                ref={aiInputRef}
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendAiMessage();
                  }
                }}
                placeholder="Ask anything about your calendar…"
                className="min-h-[52px] max-h-[140px] resize-none text-sm py-3"
                rows={2}
                disabled={aiIsLoading}
              />
              <Button
                onClick={() => sendAiMessage()}
                disabled={!aiInput.trim() || aiIsLoading}
                size="icon"
                className="h-[52px] w-10 shrink-0 bg-violet-600 hover:bg-violet-700 text-white"
              >
                {aiIsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">↵ Enter to send  •  ⇧↵ new line</p>
          </div>
        </div>
      )}

    </>
  );
}
